import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Pagination,
    Container,
    Chip,
    InputAdornment,
    FormControl,
    OutlinedInput,
    CircularProgress,
    Paper,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import CreatorGrid from '../components/CreatorGrid';
import useSearchApi from '../Hooks/useSearchApi';
import { brandGradient } from '../shared-theme/themePrimitives';

const platformsList = ['Instagram', 'YouTube', 'TikTok', 'Twitter/X', 'Facebook', 'Reddit'];
const categoriesList = ["Travel", "Fashion", "Music", "Comedy"];
const SearchPage = () => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    // const [priceRange, setPriceRange] = useState([0, 1000]);
    const [selectedPlatform, setSelectedPlatform] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [page, setPage] = useState(1);

    const { data: creators, meta, loading, fetchCreators } = useSearchApi();
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, 300);
        return () => clearTimeout(handler);
    }, [search]);

    useEffect(() => {
        fetchCreators({
            page,
            platforms: selectedPlatform ? [selectedPlatform] : [],
            categories: selectedCategory ? [selectedCategory] : [],
            keywords: debouncedSearch || "",
        });
    }, [page, selectedPlatform, selectedCategory, debouncedSearch]);

    const handlePlatformClick = (platform) => {
        setSelectedPlatform(platform === selectedPlatform ? '' : platform);
        setPage(1);
    };

    const handleCategoryClick = (category) => {
        setSelectedCategory(category === selectedCategory ? '' : category);
        setPage(1);
    };

    const handlePageChange = (_, value) => setPage(value);

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 700,
                    background: brandGradient,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline-block',
                    mb: 3,
                }}
            >
                Explore Creators
            </Typography>

            {/* Filters */}
            <Paper
                variant="outlined"
                sx={{
                    p: 2.5,
                    borderRadius: 3,
                    mb: 4,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', md: 'flex-start' },
                }}
            >
                {/* Platform + Category Chips */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip
                            label="All Platforms"
                            onClick={() => handlePlatformClick('')}
                            color={!selectedPlatform ? 'secondary' : 'default'}
                            variant={!selectedPlatform ? 'filled' : 'outlined'}
                        />
                        {platformsList.map((platform) => (
                            <Chip
                                key={platform}
                                label={platform}
                                onClick={() => handlePlatformClick(platform)}
                                color={selectedPlatform === platform ? 'secondary' : 'default'}
                                variant={selectedPlatform === platform ? 'filled' : 'outlined'}
                            />
                        ))}
                    </Box>

                    {/* Category Chips */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip
                            label="All Categories"
                            onClick={() => handleCategoryClick('')}
                            color={!selectedCategory ? 'secondary' : 'default'}
                            variant={!selectedCategory ? 'filled' : 'outlined'}
                        />
                        {categoriesList.map((category) => (
                            <Chip
                                key={category}
                                label={category}
                                onClick={() => handleCategoryClick(category)}
                                color={selectedCategory === category ? 'secondary' : 'default'}
                                variant={selectedCategory === category ? 'filled' : 'outlined'}
                            />
                        ))}
                    </Box>
                </Box>
                {/* Search Field */}
                <FormControl sx={{ width: { xs: '100%', md: '300px' } }} variant="outlined">
                    <OutlinedInput
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search creators…"
                        startAdornment={
                            <InputAdornment position="start">
                                <SearchRoundedIcon sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                        }
                    />
                </FormControl>
            </Paper>

            {/* Creator Cards */}
            {loading ? (
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress color="secondary" />
                </Box>
            ) : (
                <CreatorGrid
                    creatorTypes="All Creators"
                    creatorList={creators}
                    itemToDisplay={creators.length}
                />
            )}

            {/* Pagination */}
            {meta?.last_page > 1 && (
                <Box display="flex" justifyContent="center" mt={4}>
                    <Pagination
                        count={meta.last_page}
                        page={meta.current_page}
                        onChange={handlePageChange}
                        color="primary"
                    />
                </Box>
            )}
        </Container>
    );
};

export default SearchPage;
